/**
 * Subscription Service
 * Handles subscription management, pricing calculations, and plan logic
 * Free for personal users (≤10 properties), paid for commercial (11+)
 */

import { db, auth } from './firebase';
import { adminService } from './AdminService';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  onSnapshot,
  Timestamp
} from 'firebase/firestore';
import type {
  UserSubscription,
  PricingPlan,
  SubscriptionCalculation,
  PlanId,
  SubscriptionStatus
} from '../types/subscription';

const isProd = window.location.hostname.endsWith('canvasestate.com.au') ||
  window.location.hostname.endsWith('canvasestate.com') ||
  window.location.hostname.includes('financial-portfolio-dash.web.app');

export const COMMERCIAL_PROPERTY_THRESHOLD = 10;

export const DEFAULT_PLANS: PricingPlan[] = [
  {
    planId: 'free',
    name: 'Free',
    description: 'Free for personal use — manage up to 10 properties',
    basePrice: 0,
    additionalPropertyPrice: 0,
    includedProperties: 10,
    features: [
      'Up to 10 properties',
      'AI Property Analysis',
      'Document Storage (1GB)',
      'Historical Growth Data',
      'CSV Report Export',
      '1 User included'
    ],
    limits: {
      maxProperties: 10,
      maxUsers: 1,
      aiQueriesPerMonth: null,
      storageGB: 1
    },
    isPopular: false,
    trialDays: 0
  },
  {
    planId: 'base',
    name: 'Commercial',
    description: 'For investors and professionals with larger portfolios',
    basePrice: 49,
    additionalPropertyPrice: 0,
    includedProperties: 100,
    features: [
      'Unlimited Properties',
      'Unlimited AI Analysis',
      'Document Storage (5GB)',
      'Historical Growth Data',
      'CSV Report Export',
      'Priority Email Support',
      'Real-time Sync'
    ],
    limits: {
      maxProperties: null,
      maxUsers: 1,
      aiQueriesPerMonth: null,
      storageGB: 5
    },
    isPopular: true,
    trialDays: 0,
    stripePriceId: isProd ? 'price_1QiaXo8Ujq4NI5JCyGrTt4QK' : 'price_1ShiGp8Ujq4NI5JCyGrTt4QK'
  },
  {
    planId: 'enterprise',
    name: 'Enterprise',
    description: 'For property managers and large portfolios',
    basePrice: 199,
    additionalPropertyPrice: 3,
    includedProperties: 30,
    features: [
      '30 Properties included',
      'Priority AI Analysis',
      'Team Collaboration',
      'Custom Integrations',
      'API Access',
      'Dedicated Account Manager'
    ],
    limits: {
      maxProperties: null,
      maxUsers: 100,
      aiQueriesPerMonth: null,
      storageGB: 100
    },
    isPopular: false,
    trialDays: 0,
    stripePriceId: isProd ? 'price_1QiaXo8Ujq4NI5JCyGrTt4QK' : 'price_1ShiGq8Ujq4NI5JCsNIujz2Z'
  }
];

class SubscriptionService {

  /**
   * Initialize free subscription for new user (no trial period)
   */
  async initializeTrialSubscription(email: string, userId: string): Promise<void> {
    const now = new Date();
    const docId = email.toLowerCase();

    // Free tier: active immediately, no trial period
    const subscriptionData = {
      uid: userId,
      userId: userId, // Keep both for compatibility
      email: docId,
      planId: 'free' as PlanId,
      status: 'active' as SubscriptionStatus,
      isTrialActive: false,
      propertyCount: 0,
      calculatedMonthlyPrice: 0,
      role: 'user',
      createdAt: Timestamp.fromDate(now),
      updatedAt: Timestamp.fromDate(now),
      createdBy: 'self-registration'
    };

    // Add to authorized_users with subscription fields
    await setDoc(doc(db, 'authorized_users', docId), subscriptionData, { merge: true });

    console.log(`✓ Free subscription initialized for ${email}`);
  }

  /**
   * Get user's subscription details
   */
  async getUserSubscription(email: string): Promise<UserSubscription | null> {
    try {
      const userDoc = await getDoc(doc(db, 'authorized_users', email.toLowerCase()));

      if (!userDoc.exists()) {
        return null;
      }

      const data = userDoc.data() as any;
      console.log(`[SubscriptionService] Raw data for ${email}:`, data);

      // Helper to safely convert Firebase Timestamp to Date
      const toDate = (val: any) => {
        if (!val) return undefined;
        if (typeof val.toDate === 'function') return val.toDate();
        if (val instanceof Date) return val;
        if (val.seconds) return new Date(val.seconds * 1000);
        return new Date(val);
      };

      const sub = {
        userId: data.uid || data.userId,
        email: data.email,
        planId: data.planId || 'free',
        status: data.status || 'trial',
        isTrialActive: data.isTrialActive === true, // Force boolean
        trialStartedAt: toDate(data.trialStartedAt),
        trialEndsAt: toDate(data.trialEndsAt),
        subscriptionStartedAt: toDate(data.subscriptionStartedAt),
        currentPeriodStart: toDate(data.currentPeriodStart),
        currentPeriodEnd: toDate(data.currentPeriodEnd),
        cancelledAt: toDate(data.cancelledAt),
        stripeCustomerId: data.stripeCustomerId,
        stripeSubscriptionId: data.stripeSubscriptionId,
        propertyCount: data.propertyCount || 0,
        calculatedMonthlyPrice: data.calculatedMonthlyPrice || 0,
        createdAt: toDate(data.createdAt) || new Date(),
        updatedAt: toDate(data.updatedAt) || new Date()
      };

      console.log(`[SubscriptionService] Parsed subscription:`, sub);
      return sub;
    } catch (error) {
      console.error('Error fetching user subscription:', error);
      return null;
    }
  }

  /**
   * Check if user's trial has expired
   */
  isTrialExpired(subscription: UserSubscription): boolean {
    if (!subscription.isTrialActive || !subscription.trialEndsAt) {
      return false;
    }
    return new Date() > subscription.trialEndsAt;
  }

  /**
   * Check if user has active subscription (free, paid, or permanent)
   */
  hasActiveAccess(subscription: UserSubscription | null): boolean {
    if (!subscription) return false;

    // Free plan users always have access (no trial expiry)
    if (subscription.planId === 'free') {
      return true;
    }

    // Active trial (legacy — for users who still have trial status)
    if (subscription.isTrialActive && !this.isTrialExpired(subscription)) {
      return true;
    }

    // Active paid subscription or permanent
    return subscription.status === 'active' || subscription.status === 'permanent';
  }

  /**
   * Get all pricing plans
   */
  async getPricingPlans(): Promise<PricingPlan[]> {
    try {
      const plansSnapshot = await getDocs(collection(db, 'pricing_plans'));
      const plans: PricingPlan[] = [];

      plansSnapshot.forEach((doc) => {
        const data = doc.data() as any;
        plans.push({
          planId: data.planId,
          name: data.name,
          basePrice: data.basePrice,
          additionalPropertyPrice: data.additionalPropertyPrice,
          includedProperties: data.includedProperties,
          features: data.features,
          limits: data.limits,
          isPopular: data.isPopular,
          trialDays: data.trialDays,
          stripePriceId: data.stripePriceId || 'price_1QiaXo8Ujq4NI5JCyGrTt4QK'
        });
      });

      if (plans.length === 0) {
        return DEFAULT_PLANS;
      }

      // Sort by priority
      return plans.sort((a, b) => (a as any).priority - (b as any).priority);
    } catch (error) {
      console.error('Error fetching pricing plans:', error);
      return DEFAULT_PLANS;
    }
  }

  /**
   * Get specific pricing plan
   */
  async getPricingPlan(planId: PlanId): Promise<PricingPlan | null> {
    try {
      const planDoc = await getDoc(doc(db, 'pricing_plans', planId));

      if (!planDoc.exists()) {
        const fallback = DEFAULT_PLANS.find(p => p.planId === planId);
        return fallback || null;
      }

      const data = planDoc.data() as any;
      return {
        planId: data.planId,
        name: data.name,
        basePrice: data.basePrice,
        additionalPropertyPrice: data.additionalPropertyPrice,
        includedProperties: data.includedProperties,
        features: data.features,
        limits: data.limits,
        isPopular: data.isPopular,
        trialDays: data.trialDays,
        stripePriceId: data.stripePriceId || 'price_1QiaXo8Ujq4NI5JCyGrTt4QK'
      };
    } catch (error) {
      console.error('Error fetching pricing plan:', error);
      const fallback = DEFAULT_PLANS.find(p => p.planId === planId);
      return fallback || null;
    }
  }

  /**
   * Calculate subscription price based on property count
   */
  calculatePrice(
    propertyCount: number,
    plan: PricingPlan
  ): SubscriptionCalculation {
    const additionalProperties = Math.max(0, propertyCount - plan.includedProperties);
    const additionalPropertyCost = additionalProperties * plan.additionalPropertyPrice;
    const totalMonthlyPrice = plan.basePrice + additionalPropertyCost;
    const annualPrice = Math.round(totalMonthlyPrice * 12 * 0.83); // 17% discount

    return {
      basePrice: plan.basePrice,
      includedProperties: plan.includedProperties,
      totalProperties: propertyCount,
      additionalProperties,
      additionalPropertyCost,
      totalMonthlyPrice,
      annualPrice
    };
  }

  /**
   * Update property count and recalculate price
   */
  async updatePropertyCount(email: string, propertyCount: number): Promise<void> {
    try {
      const subscription = await this.getUserSubscription(email);
      if (!subscription) {
        throw new Error('Subscription not found');
      }

      const plan = await this.getPricingPlan(subscription.planId);
      if (!plan) {
        throw new Error('Pricing plan not found');
      }

      const calculation = this.calculatePrice(propertyCount, plan);

      await updateDoc(doc(db, 'authorized_users', email.toLowerCase()), {
        propertyCount,
        calculatedMonthlyPrice: calculation.totalMonthlyPrice,
        updatedAt: Timestamp.fromDate(new Date())
      });

      console.log(`✓ Updated property count to ${propertyCount} for ${email}`);
    } catch (error) {
      console.error('Error updating property count:', error);
      throw error;
    }
  }

  /**
   * Check if user can add more properties based on plan limits
   */
  async canAddProperty(email: string, currentPropertyCount: number): Promise<{
    allowed: boolean;
    reason?: string;
    requiresUpgrade?: boolean;
  }> {
    const subscription = await this.getUserSubscription(email);

    if (!subscription) {
      return { allowed: false, reason: 'No subscription found' };
    }

    // Check if access is expired (legacy trial check)
    if (!this.hasActiveAccess(subscription)) {
      return { allowed: false, reason: 'Subscription expired or inactive' };
    }

    // Free plan: enforce 10-property limit
    if (subscription.planId === 'free' && currentPropertyCount >= COMMERCIAL_PROPERTY_THRESHOLD) {
      return {
        allowed: false,
        requiresUpgrade: true,
        reason: `Free plan supports up to ${COMMERCIAL_PROPERTY_THRESHOLD} properties. Upgrade to Commercial for unlimited properties.`
      };
    }

    const plan = await this.getPricingPlan(subscription.planId);
    if (!plan) {
      return { allowed: false, reason: 'Pricing plan not found' };
    }

    // Check plan limits
    if (plan.limits.maxProperties !== null && currentPropertyCount >= plan.limits.maxProperties) {
      return {
        allowed: false,
        requiresUpgrade: true,
        reason: `You've reached the maximum of ${plan.limits.maxProperties} properties for your plan`
      };
    }

    return { allowed: true };
  }

  /**
   * Upgrade user from trial to paid subscription
   */
  async upgradeFromTrial(email: string, planId: PlanId): Promise<void> {
    const subscriptionStartedAt = new Date();
    const currentPeriodStart = new Date();
    const currentPeriodEnd = new Date();
    currentPeriodEnd.setMonth(currentPeriodEnd.getMonth() + 1);

    await updateDoc(doc(db, 'authorized_users', email.toLowerCase()), {
      planId,
      status: 'active',
      isTrialActive: false,
      subscriptionStartedAt: Timestamp.fromDate(subscriptionStartedAt),
      currentPeriodStart: Timestamp.fromDate(currentPeriodStart),
      currentPeriodEnd: Timestamp.fromDate(currentPeriodEnd),
      updatedAt: Timestamp.fromDate(new Date())
    });

    console.log(`✓ Upgraded ${email} from trial to ${planId}`);
  }

  /**
   * Create Stripe checkout session via Firebase Extension
   * The extension listens to customers/{uid}/checkout_sessions and creates Stripe session
   */
  async createCheckoutSession({
    priceId,
    successUrl,
    cancelUrl,
    mode = 'subscription',
    metadata = {}
  }: {
    priceId: string;
    successUrl: string;
    cancelUrl: string;
    mode?: 'subscription' | 'payment';
    metadata?: Record<string, string>;
  }): Promise<string> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated to create checkout session');
    }

    // Create checkout session document
    // Firebase extension watches this collection and creates Stripe session
    const checkoutSessionRef = await addDoc(
      collection(db, 'customers', user.uid, 'checkout_sessions'),
      {
        price: priceId,
        success_url: successUrl,
        cancel_url: cancelUrl,
        mode,
        metadata,
        allow_promotion_codes: true,
        customer_email: user.email, // Pre-fill email for logged-in users
        created: Timestamp.now()
      }
    );

    // Wait for the extension to populate the session URL
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Checkout session creation timed out'));
      }, 30000); // 30 second timeout

      const unsubscribe = onSnapshot(
        checkoutSessionRef,
        (snap) => {
          const data = snap.data() as any;
          if (data?.url) {
            clearTimeout(timeout);
            unsubscribe();
            resolve(data.url);
          } else if (data?.error) {
            clearTimeout(timeout);
            unsubscribe();
            console.error('Stripe Checkout Error Data:', data);
            // Safely extract error message
            const errorMessage = data.error?.message ||
              (typeof data.error === 'string' ? data.error : 'Checkout session creation failed');
            reject(new Error(errorMessage));
          }
        },
        (error) => {
          clearTimeout(timeout);
          console.error('Firestore Observer Error:', error);
          reject(error);
        }
      );
    });
  }

  /**
   * Create customer portal link for managing subscriptions
   * The extension creates this via customers/{uid}/checkout_sessions
   */
  async createPortalLink(returnUrl: string): Promise<string> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated to access customer portal');
    }

    const portalSessionRef = await addDoc(
      collection(db, 'customers', user.uid, 'checkout_sessions'),
      {
        type: 'portal',
        return_url: returnUrl,
        created: Timestamp.now()
      }
    );

    // Wait for the extension to populate the portal URL
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Portal link creation timed out'));
      }, 30000);

      const unsubscribe = onSnapshot(
        portalSessionRef,
        (snap) => {
          const data = snap.data() as any;
          if (data?.url) {
            clearTimeout(timeout);
            unsubscribe();
            resolve(data.url);
          } else if (data?.error) {
            clearTimeout(timeout);
            unsubscribe();
            reject(new Error(data.error.message || 'Portal link creation failed'));
          }
        },
        (error) => {
          clearTimeout(timeout);
          reject(error);
        }
      );
    });
  }

  /**
   * Get active Stripe subscriptions for current user
   * Firebase extension syncs these from Stripe
   */
  async getStripeSubscriptions(): Promise<any[]> {
    const user = auth.currentUser;
    if (!user) {
      return [];
    }

    try {
      const subscriptionsSnapshot = await getDocs(
        collection(db, 'customers', user.uid, 'subscriptions')
      );

      const subscriptions: any[] = [];
      subscriptionsSnapshot.forEach((doc) => {
        subscriptions.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return subscriptions;
    } catch (error) {
      console.error('Error fetching Stripe subscriptions:', error);
      return [];
    }
  }

  /**
   * ADMIN: Get all users with their subscription info
   */
  async getAllUsersWithSubscriptions(): Promise<any[]> {
    try {
      // 1. Fetch data through AdminService (Cloud Functions) to bypass permission issues
      const [authorizedUsers, accounts] = await Promise.all([
        adminService.getAuthorizedUsers(),
        adminService.getAccounts()
      ]);

      console.log(`[SubscriptionService] Fetched via AdminService: users=${authorizedUsers.length}, accounts=${accounts.length}`);

      // 2. Build mapping from accounts
      const userAccountMap = new Map<string, { accountId: string; accountType: string; accountName: string; userCount: number }>();

      accounts.forEach(account => {
        const userCount = account.connectedUsers?.length || 0;

        account.connectedUsers?.forEach(conn => {
          if (conn.email) {
            const email = conn.email.toLowerCase();
            const type = conn.type || 'shared';

            // Prioritize 'owner' status if user is in multiple accounts
            const existing = userAccountMap.get(email);
            if (!existing || type === 'owner') {
              userAccountMap.set(email, {
                accountId: account.id,
                accountType: type,
                accountName: account.name,
                userCount: userCount
              });
            }
          }
        });
      });

      // 3. Process authorized users and merge with account info
      const resultsMap = new Map<string, any>();

      // Helper to safely convert various timestamp formats to Date
      const safeToDate = (val: any): Date | undefined => {
        if (!val) return undefined;
        if (typeof val.toDate === 'function') return val.toDate();
        if (val instanceof Date) return val;
        if (val.seconds) return new Date(val.seconds * 1000);
        if (val._seconds) return new Date(val._seconds * 1000);
        if (typeof val === 'string') return new Date(val);
        return undefined;
      };

      authorizedUsers.forEach(user => {
        const email = user.email.toLowerCase();

        // Subscription logic
        let trialDaysRemaining = 0;
        let isExpired = false;
        const trialEndsAtDate = safeToDate(user.trialEndsAt);
        if (trialEndsAtDate) {
          const now = new Date();
          const daysRemaining = Math.ceil((trialEndsAtDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          trialDaysRemaining = Math.max(0, daysRemaining);
          isExpired = daysRemaining < 0;
        }

        const userObj: any = {
          email,
          role: user.role || 'user',
          planId: user.planId || 'free',
          status: (user.status as any) || 'trial',
          isTrialActive: user.isTrialActive || false,
          trialStartedAt: safeToDate(user.trialStartedAt),
          trialEndsAt: trialEndsAtDate,
          trialDaysRemaining,
          isExpired,
          subscriptionStartedAt: safeToDate(user.subscriptionStartedAt),
          currentPeriodStart: safeToDate(user.currentPeriodStart),
          currentPeriodEnd: safeToDate(user.currentPeriodEnd),
          stripeCustomerId: user.stripeCustomerId,
          stripeSubscriptionId: user.stripeSubscriptionId,
          propertyCount: user.propertyCount || 0,
          calculatedMonthlyPrice: user.calculatedMonthlyPrice || 0,
          hasActiveSubscription: user.status === 'active' || user.status === 'permanent',
          createdAt: safeToDate(user.createdAt) || new Date(),
          uid: user.uid
        };

        // Attach account info if exists
        const accInfo = userAccountMap.get(email);
        if (accInfo) {
          userObj.accountId = accInfo.accountId;
          userObj.accountType = accInfo.accountType.toLowerCase();
          userObj.accountName = accInfo.accountName;
          userObj.usersInAccount = accInfo.userCount;
        }

        resultsMap.set(email, userObj);
      });

      // 4. Handle "shell users" (owners in accounts but not in authorized_users)
      userAccountMap.forEach((accInfo, email) => {
        if (!resultsMap.has(email) && accInfo.accountType === 'owner') {
          resultsMap.set(email, {
            email,
            role: 'user',
            planId: 'free',
            status: 'trial',
            isTrialActive: false,
            trialDaysRemaining: 0,
            isExpired: false,
            hasActiveSubscription: false,
            propertyCount: 0,
            calculatedMonthlyPrice: 0,
            createdAt: new Date(),
            accountId: accInfo.accountId,
            accountType: 'owner',
            accountName: accInfo.accountName,
            usersInAccount: accInfo.userCount
          });
        }
      });

      // 5. Filter for owners and users without specific account mapping (admins, new users)
      // We only want to exclude 'shared' users who are explicitly not the subscription owners
      const results = Array.from(resultsMap.values());
      const filteredResults = results.filter(u => u.accountType !== 'shared');

      console.log(`[SubscriptionService] Final processed users (excluding shared): ${filteredResults.length}`);

      return filteredResults.sort((a, b) => {
        if (a.isExpired && !b.isExpired) return -1;
        if (!a.isExpired && b.isExpired) return 1;
        return a.trialDaysRemaining - b.trialDaysRemaining;
      });
    } catch (error) {
      console.error('Error fetching all users with subscriptions:', error);
      return [];
    }
  }

  /**
   * Get trial days remaining for a subscription
   */
  getTrialDaysRemaining(subscription: UserSubscription): number {
    if (!subscription.trialEndsAt) return 0;

    const now = new Date();
    const endsAt = subscription.trialEndsAt;
    const daysRemaining = Math.ceil((endsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    return Math.max(0, daysRemaining);
  }

  /**
   * Subscribe to real-time subscription updates for a user
   */
  subscribeToUserSubscription(
    email: string,
    callback: (subscription: UserSubscription | null) => void
  ): () => void {
    const userDocRef = doc(db, 'authorized_users', email.toLowerCase());

    // Helper to safely convert various timestamp formats to Date
    const safeToDate = (val: any): Date | undefined => {
      if (!val) return undefined;
      if (typeof val.toDate === 'function') return val.toDate();
      if (val instanceof Date) return val;
      if (val.seconds) return new Date(val.seconds * 1000);
      if (typeof val === 'string') return new Date(val);
      return undefined;
    };

    const unsubscribe = onSnapshot(userDocRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data() as any;
        console.log(`[SubscriptionService] Real-time update for ${email}:`, {
          isTrialActive: data.isTrialActive,
          trialEndsAt: data.trialEndsAt,
          status: data.status
        });
        const subscription: UserSubscription = {
          userId: data.uid || data.userId,
          email: data.email,
          planId: data.planId || 'free',
          status: data.status || 'trial',
          isTrialActive: data.isTrialActive === true, // Force boolean check
          trialStartedAt: safeToDate(data.trialStartedAt),
          trialEndsAt: safeToDate(data.trialEndsAt),
          subscriptionStartedAt: safeToDate(data.subscriptionStartedAt),
          currentPeriodStart: safeToDate(data.currentPeriodStart),
          currentPeriodEnd: safeToDate(data.currentPeriodEnd),
          cancelledAt: safeToDate(data.cancelledAt),
          stripeCustomerId: data.stripeCustomerId,
          stripeSubscriptionId: data.stripeSubscriptionId,
          propertyCount: data.propertyCount || 0,
          calculatedMonthlyPrice: data.calculatedMonthlyPrice || 0,
          createdAt: safeToDate(data.createdAt) || new Date(),
          updatedAt: safeToDate(data.updatedAt) || new Date()
        };
        callback(subscription);
      } else {
        callback(null);
      }
    });

    return unsubscribe;
  }

  /**
   * Get subscription period days remaining
   */
  getSubscriptionDaysRemaining(subscription: UserSubscription): number {
    if (!subscription.currentPeriodEnd) return 0;

    const now = new Date();
    const endsAt = subscription.currentPeriodEnd;
    const daysRemaining = Math.ceil((endsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    return Math.max(0, daysRemaining);
  }

  /**
   * ADMIN: Manually sync a user's subscription from Stripe
   * Useful when subscription is created manually in Stripe console
   */
  async syncUserSubscriptionFromStripe(email: string, uid: string): Promise<{ success: boolean; message: string }> {
    try {
      // Get Stripe subscriptions for the user
      const subscriptionsQuery = query(
        collection(db, 'customers', uid, 'subscriptions'),
        where('status', 'in', ['active', 'trialing'])
      );

      const subscriptionsSnapshot = await getDocs(subscriptionsQuery);

      if (subscriptionsSnapshot.empty) {
        return {
          success: false,
          message: 'No active Stripe subscriptions found for this user'
        };
      }

      // Get the most recent active subscription
      const subscriptionDoc = subscriptionsSnapshot.docs[0];
      const stripeSubscription = subscriptionDoc.data() as any;

      // Extract plan ID from metadata
      const planId = stripeSubscription.items?.[0]?.price?.product?.metadata?.planId ||
        stripeSubscription.metadata?.planId ||
        'base';

      // Sync to authorized_users
      await updateDoc(doc(db, 'authorized_users', email.toLowerCase()), {
        planId: planId,
        status: stripeSubscription.status === 'trialing' ? 'trial' : 'active',
        isTrialActive: stripeSubscription.status === 'trialing',
        stripeCustomerId: uid,
        stripeSubscriptionId: subscriptionDoc.id,
        subscriptionStartedAt: stripeSubscription.created
          ? Timestamp.fromMillis(stripeSubscription.created * 1000)
          : Timestamp.now(),
        currentPeriodStart: stripeSubscription.current_period_start
          ? Timestamp.fromMillis(stripeSubscription.current_period_start * 1000)
          : undefined,
        currentPeriodEnd: stripeSubscription.current_period_end
          ? Timestamp.fromMillis(stripeSubscription.current_period_end * 1000)
          : undefined,
        updatedAt: Timestamp.now()
      });

      return {
        success: true,
        message: `Successfully synced subscription (${planId}, status: ${stripeSubscription.status})`
      };
    } catch (error) {
      console.error('Error syncing subscription:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to sync subscription'
      };
    }
  }

  /**
   * ADMIN: Manually update user's subscription status
   */
  async updateSubscriptionStatus(
    email: string,
    data: {
      status: SubscriptionStatus;
      planId?: PlanId;
      currentPeriodEnd?: Date;
      isTrialActive?: boolean;
      trialEndsAt?: Date;
    }
  ): Promise<void> {
    try {
      const updateData: any = {
        status: data.status,
        updatedAt: Timestamp.now()
      };

      if (data.planId) updateData.planId = data.planId;
      if (data.currentPeriodEnd) updateData.currentPeriodEnd = Timestamp.fromDate(data.currentPeriodEnd);
      if (data.isTrialActive !== undefined) updateData.isTrialActive = data.isTrialActive;
      if (data.trialEndsAt) updateData.trialEndsAt = Timestamp.fromDate(data.trialEndsAt);

      // Special handling for permanent status
      if (data.status === 'permanent') {
        updateData.isTrialActive = false;
        // Set a very far off date if end date isn't provided
        if (!data.currentPeriodEnd) {
          const farOffDate = new Date();
          farOffDate.setFullYear(farOffDate.getFullYear() + 100);
          updateData.currentPeriodEnd = Timestamp.fromDate(farOffDate);
        }
      }

      await updateDoc(doc(db, 'authorized_users', email.toLowerCase()), updateData);
      console.log(`✓ Manually updated subscription status for ${email} to ${data.status}`);
    } catch (error) {
      console.error('Error updating subscription status:', error);
      throw error;
    }
  }
}

export const subscriptionService = new SubscriptionService();
export default subscriptionService;
